import { useEffect, useState } from 'react'
import {
  Box,
  Chip,
  Paper,
  Skeleton,
  Tooltip,
  Typography
} from '@mui/material'
import { AutoAwesome, InfoOutlined } from '@mui/icons-material'
import StockLogo from '../imgs/stock-logo'
import TickerLink from '../ticker-link'
import { StockService } from '../../services/stock.service'
import { useError } from '../../context/error'
import type { StockInfo } from '../../_types/stock-info.type'

interface VNStockOfDayProps {
  ticker?: string
}

const EXPLAINER =
  'Every day we find the 4% of the market that is driving performance. The VN "Stock of the Day" is an idea generated by our proprietary system at the close each day. It is a stock demonstrating all the characteristics we look for: it is rising, in the right sector, scores well within our ranking systems, has completed one of our favorite patterns, and is often at/near the pivot entry point. It is meant to demonstrate everything you should be looking for in a trade setup and is often being traded in one of our hypothetical portfolios.'

export default function VNStockOfDay({ ticker }: VNStockOfDayProps) {
  const { setError } = useError()
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [info, setInfo] = useState<StockInfo | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!ticker) return
    let cancelled = false
    setLoading(true)
    const stockService = new StockService(setError)

    Promise.all([
      stockService.getStockLogo(ticker),
      stockService.getStockInfo(ticker)
    ]).then(([logoRes, infoRes]) => {
      if (cancelled) return
      if (logoRes?.url) setLogoUrl(logoRes.url)
      if (infoRes) setInfo(infoRes)
      setLoading(false)
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker])

  if (!ticker) return null

  const companyName: string | undefined = info?.profile?.name
  const price: number | undefined = info?.currentPrice
    ? Number(info.currentPrice)
    : undefined
  const percentChangeRaw: string | undefined = info?.quote?.percent_change
  const percentChangeNum =
    percentChangeRaw !== undefined ? parseFloat(percentChangeRaw) : undefined
  const changeColor =
    percentChangeNum === undefined
      ? 'text.primary'
      : percentChangeNum >= 0
      ? 'success.main'
      : 'error.main'

  return (
    <Paper
      elevation={6}
      sx={{
        borderTop: theme => `4px solid ${theme.palette.primary.main}`,
        overflow: 'hidden',
        mb: 3
      }}
    >
      <Box p={3} display='flex' flexDirection={{ xs: 'column', md: 'row' }} gap={3} alignItems={{ xs: 'flex-start', md: 'center' }}>
        <Box
          sx={{
            width: { xs: 96, md: 120 },
            height: { xs: 96, md: 120 },
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {loading ? (
            <Skeleton variant='rectangular' width='100%' height='100%' />
          ) : (
            <StockLogo
              src={logoUrl || ''}
              alt={ticker}
              sx={{ width: '100%', height: '100%' }}
            />
          )}
        </Box>

        <Box flex={1} minWidth={0}>
          <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
            <Chip
              size='small'
              color='primary'
              icon={<AutoAwesome sx={{ fontSize: 16 }} />}
              label='VN Stock of the Day'
              sx={{ fontWeight: 'bold' }}
            />
            <Tooltip title={EXPLAINER} arrow placement='right'>
              <InfoOutlined fontSize='small' color='action' sx={{ cursor: 'help' }} />
            </Tooltip>
          </Box>

          <Box mt={1} display='flex' alignItems='baseline' gap={1.5} flexWrap='wrap'>
            <Typography variant='h3' fontWeight='bold'>
              {ticker}
            </Typography>
            {loading ? (
              <Skeleton variant='text' width={200} height={32} />
            ) : (
              companyName && (
                <Typography variant='h6' color='text.secondary' noWrap>
                  {companyName}
                </Typography>
              )
            )}
          </Box>

          {!loading && price !== undefined && (
            <Box mt={0.5} display='flex' alignItems='baseline' gap={1.5}>
              <Typography variant='h5' fontWeight={600}>
                ${price.toFixed(2)}
              </Typography>
              {percentChangeRaw !== undefined && (
                <Typography variant='subtitle1' color={changeColor} fontWeight={600}>
                  {percentChangeNum !== undefined && percentChangeNum >= 0 ? '+' : ''}
                  {percentChangeRaw}%
                </Typography>
              )}
            </Box>
          )}

          <Box mt={1.5}>
            <TickerLink display='chip' ticker={ticker} />
          </Box>
        </Box>
      </Box>
    </Paper>
  )
}
